import {
    TTableColumnFilterExpessionGroupIdentificator,
    TTableColumnFilterExpression,
    TTableColumnFilterExpressionDto,
    TTableColumnFilterOperand,
    ITableColumnFilterOperandObject,
} from '../table-column-filter-types';
import {TTableColumnFilters} from './use-table-column-filter-types';

type TTransformFilterExpressionToFilterDtoFunction = (
    exp: TTableColumnFilterExpression) => TTableColumnFilterExpressionDto;
export const transformFilterExpressionToFilterDto:
    TTransformFilterExpressionToFilterDtoFunction = (expression: TTableColumnFilterExpression) => {
        const [identifier, ...operands] = expression;

        const expressionArrays = operands.filter(oper => Array.isArray(oper)) as TTableColumnFilterExpression[];
        const rules = operands.filter(oper => !Array.isArray(oper)) as ITableColumnFilterOperandObject[];

        const groups: TTableColumnFilterExpressionDto[] = [];

        (expressionArrays ?? []).forEach(expArray => {
            groups.push(transformFilterExpressionToFilterDto(expArray));
        });

        return {
            connectBy: identifier.connectBy,
            groups,
            rules,
        };
    };

type TTransformFilterDtoToFilterExpressionFunction = (
        dto: TTableColumnFilterExpressionDto) => TTableColumnFilterExpression;
export const transformFilterDtoToFilterExpression:
    TTransformFilterDtoToFilterExpressionFunction = (dto: TTableColumnFilterExpressionDto) => {
        const {connectBy, groups, rules} = dto;

        const identifier: TTableColumnFilterExpessionGroupIdentificator = {
            connectBy,
        };

        const operands: TTableColumnFilterOperand[] = rules?.length ? rules.map(
            rule => ({...rule}),
        ) as TTableColumnFilterOperand[] : [];

        if (groups?.length) {
            groups.forEach(groupDto => {
                operands.push(transformFilterDtoToFilterExpression(groupDto));
            });
        }

        return [
            identifier,
            ...operands,
        ];
    };

export const transformFiltersFromRequestToColumnFiltersObject = (
    filtersFromRequest: TTableColumnFilterExpressionDto,
) => {
    const {groups} = filtersFromRequest;

    const extractColumnNameFromExpression: (expGroups: TTableColumnFilterExpression) => string | undefined = (
        expGroups: TTableColumnFilterExpression,
    ) => {
        let name;
        const [, ...operands] = expGroups;
        operands.forEach(operand => {
            if (Array.isArray(operand)) name = extractColumnNameFromExpression(operand);
            else {
                const {columnName} = operand;
                name = columnName;
            }
        });
        return name;
    };

    const tableColumnFilters: TTableColumnFilters = Object.fromEntries(groups.map(group => {
        const filterDto = transformFilterDtoToFilterExpression(group);
        return [
            extractColumnNameFromExpression(filterDto),
            filterDto,
        ];
    }).filter(([key]) => !!key));

    return tableColumnFilters;
};

export const adaptFiltersForRequest = (filters: TTableColumnFilters | undefined) => {
    if (!filters) return undefined;

    const filtersTransformedToDto = transformFilterExpressionToFilterDto(
        Object.entries(filters).reduce((acc, [, filterValue]) => {
            if (Array.isArray(filterValue) && filterValue.length === 1) return acc;
            return [
                ...acc, filterValue,
            ] as TTableColumnFilterExpression;
        }, [{connectBy: 'AND'}] as TTableColumnFilterExpression),
    );

    if (!filtersTransformedToDto.rules.length && !filtersTransformedToDto.groups.length) return undefined;

    return filtersTransformedToDto;
};
